import type { CSSProperties, KeyboardEvent } from 'react';
import { REACTION_META, type CharacterId, type ReactionId } from '../../theater/catalog';
import CharacterSilhouette from './CharacterSilhouette';
import './TheaterCharacter.css';

export type CharacterAnimState =
  | 'idle'
  | 'walk'
  | 'sit'
  | 'throw'
  | 'enter'
  | 'leave'
  | ReactionId;

const REACTION_STATES = new Set<string>(Object.keys(REACTION_META));

function isReactionState(state: CharacterAnimState): state is ReactionId {
  return REACTION_STATES.has(state);
}

export default function TheaterCharacter({
  character,
  username,
  state = 'sit',
  selected = false,
  row = 1,
  onSelect,
  style,
}: {
  character: CharacterId;
  username: string;
  state?: CharacterAnimState;
  selected?: boolean;
  row?: number;
  onSelect?: () => void;
  style?: CSSProperties;
}) {
  const reactionEmoji = isReactionState(state) ? REACTION_META[state].emoji : null;

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.();
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={`tt-char tt-char--${character} tt-char-state--${state}${selected ? ' is-selected' : ''}`}
      data-row={row}
      style={{ ...style }}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      aria-label={username}
      title={username}
    >
      <span className="tt-char-body" aria-hidden>
        <CharacterSilhouette character={character} className="tt-char-silhouette" />
      </span>
      {reactionEmoji && (
        <span className="tt-char-reaction" aria-hidden>
          {reactionEmoji}
        </span>
      )}
      <span className="tt-char-name">{username}</span>
    </div>
  );
}
